import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Wallet, Coins, Activity, TrendingUp, Users, Gamepad2, AlertTriangle, Copy, Check, Share2 } from "lucide-react";
import { fmtMoney } from "@/lib/utils";
import { StarClubPositions } from "@/components/StarClubPositions";

function Stat({ label, value, sub, testId, Icon }) {
  return (
    <div className="lt-card p-6" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="overline">{label}</div>
        {Icon && <Icon size={16} className="text-[#D4AF37]" />}
      </div>
      <div className="font-display text-4xl tabular">{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-2">{sub}</div>}
    </div>
  );
}

const INCOME_LABELS = {
  team: "Team",
  royalty: "Royalty",
  star_club: "Global Club",
  sclub: "Star Club",
  play: "Play",
  donation_club: "Donation Club",
};

export default function Dashboard() {
  const { user } = useAuth();
  const cacheKey = `lt_dash_${user?.id || "anon"}`;
  const cached = (() => {
    try { return JSON.parse(localStorage.getItem(cacheKey) || "null"); } catch (_) { return null; }
  })();
  const [wallets, setWallets] = useState(cached?.wallets ?? null);
  const [income, setIncome] = useState(cached?.income ?? { summary: { team: 0, royalty: 0, star_club: 0, sclub: 0, play: 0, donation_club: 0 } });
  const [lpFund, setLpFund] = useState(cached?.lpFund ?? null);
  const [playCard, setPlayCard] = useState(cached?.playCard ?? null);
  const [sclub, setSclub] = useState(cached?.sclub ?? null);

  useEffect(() => {
    let alive = true;
    const persist = (patch) => {
      try {
        const prev = JSON.parse(localStorage.getItem(cacheKey) || "{}");
        localStorage.setItem(cacheKey, JSON.stringify({ ...prev, ...patch }));
      } catch (err) { console.debug("Dashboard cache persist failed:", err?.message); }
    };
    const load = async () => {
      // allSettled so one slow/failed endpoint never blanks the others. Each balance
      // updates independently and the last good value is cached, so users never see a
      // sudden "$0.00" flash while the server is busy or right after a reload.
      const [w, ih, lp, pc, sc] = await Promise.allSettled([
        api.get("/me/wallets"),
        api.get("/me/income-history"),
        api.get("/me/lp-fund"),
        api.get("/me/play-card"),
        api.get("/me/star-club-new"),
      ]);
      if (!alive) return;
      if (w.status === "fulfilled") { setWallets(w.value.data); persist({ wallets: w.value.data }); }
      if (ih.status === "fulfilled") { setIncome(ih.value.data); persist({ income: ih.value.data }); }
      if (lp.status === "fulfilled") { setLpFund(lp.value.data); persist({ lpFund: lp.value.data }); }
      if (pc.status === "fulfilled") { setPlayCard(pc.value.data); persist({ playCard: pc.value.data }); }
      if (sc.status === "fulfilled") { setSclub(sc.value.data); persist({ sclub: sc.value.data }); }
    };
    load();
    return () => { alive = false; };
  }, [cacheKey]);

  const earningCap = Number(user?.earning_cap || 0);
  const earnedSoFar = Number(user?.earnings_so_far || 0);
  const remainingBalance = Math.max(0, earningCap - earnedSoFar);
  const nftActive = Number(playCard?.principal || 0) > 0;

  const referralLink = user?.user_id
    ? `${window.location.origin}/register?ref=${user.user_id}`
    : "";
  const [copied, setCopied] = useState(false);
  const copyReferral = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = referralLink;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="overline" data-testid="dash-welcome-overline">Welcome back, {user?.full_name?.split(" ")[0]}</div>
        <h1 className="font-display text-4xl mt-1">Your Member Dashboard</h1>
      </div>

      <div className="lt-card p-6" data-testid="referral-link-card">
        <div className="flex items-center gap-2 mb-3">
          <Share2 size={16} className="text-[#D4AF37]" />
          <div className="overline">Your Referral Link</div>
        </div>
        <div className="text-sm text-neutral-400 mb-4 max-w-2xl">
          Share this link to invite new members below you. When they open it, your
          Sponsor / Upline ID (<span className="gold-text tabular">{user?.user_id}</span>) is
          filled in automatically on the registration page.
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <div
            className="flex-1 px-4 py-3 rounded-lg border border-white/10 bg-white/[0.03] text-sm tabular break-all text-neutral-200"
            data-testid="referral-link-text"
          >
            {referralLink || "—"}
          </div>
          <button
            onClick={copyReferral}
            disabled={!referralLink}
            className={`lt-btn-primary inline-flex items-center justify-center gap-2 shrink-0 ${copied ? "opacity-90" : ""}`}
            data-testid="referral-copy-btn"
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? "Copied!" : "Copy Link"}
          </button>
        </div>
      </div>

      {user?.retopup_required && (
        <div
          className="lt-card p-5 border border-amber-500/40 bg-amber-500/[0.06] flex items-center justify-between flex-wrap gap-4"
          data-testid="retopup-banner"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-amber-300 mt-0.5 shrink-0" />
            <div>
              <div className="overline text-amber-300">Re-Top Up Required</div>
              <div className="text-sm text-neutral-300 mt-1 max-w-2xl">
                You've reached your earning cap (${earnedSoFar.toFixed(2)} / ${earningCap.toFixed(2)}).
                Network &amp; team income is paused until you re-top up with the same or a higher
                package (+ $1 Global Club entry).
              </div>
            </div>
          </div>
          <Link to="/app/packages" className="lt-btn-primary" data-testid="retopup-cta">
            Re-Top Up Now
          </Link>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        <div className="lt-card p-6" data-testid="main-wallet-card">
          <div className="flex items-center justify-between mb-3">
            <div className="overline">Main Wallet</div>
            <Wallet size={16} className="text-[#D4AF37]" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Total Earned</div>
              <div className="font-display text-3xl tabular mt-1" data-testid="main-wallet-total-earned">${earnedSoFar.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Available Balance</div>
              <div className="font-display text-3xl tabular mt-1 gold-text" data-testid="main-wallet-balance">${(wallets?.main || 0).toFixed(2)}</div>
            </div>
          </div>
          <div className="text-xs text-neutral-500 mt-2">Withdrawable balance shown on the right</div>
        </div>
        <Stat label="NFT Card Fund" value={`$${(wallets?.lp || 0).toFixed(2)}`} sub="From play cycles" testId="lp-wallet-balance" Icon={Activity} />
        <Stat label="Deposit Balance" value={`$${(wallets?.deposit || 0).toFixed(2)}`} sub="For package & game" testId="deposit-balance" Icon={Coins} />
      </div>

      <div className="lt-card p-6" data-testid="lp-fund-card">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="overline">Global LP Pool</div>
          <div className="font-display text-4xl tabular gold-text" data-testid="lp-global-pool">
            ${(lpFund?.global_lp_pool || 0).toFixed(2)}
          </div>
        </div>
      </div>

      <div className="lt-card p-6">
        <div className="overline mb-3">Earnings Breakdown</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {["team", "royalty", "star_club", "sclub", "donation_club"].map((k) => (
            <div key={k} data-testid={`income-summary-${k}`}>
              <div className="overline">{INCOME_LABELS[k]}</div>
              <div className="font-display text-2xl tabular mt-1">${fmtMoney(income.summary?.[k] || 0)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="lt-card p-6">
        <div className="overline mb-3">Membership</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 items-baseline">
          <div>
            <span className="inline-block font-display text-2xl font-bold gold-text px-3 py-1 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30" data-testid="membership-package-name">{user?.package || "No package"}</span>
            <div className="text-sm text-neutral-400 mt-2">
              {user?.package_value ? `$${user.package_value} active` : "Subscribe to unlock benefits"}
            </div>
          </div>
          <div>
            <div className="overline">Earning Cap</div>
            <div className="tabular mt-1" data-testid="dash-earning-cap">${earningCap.toFixed(2)}</div>
          </div>
          <div>
            <div className="overline">Earned so far</div>
            <div className="tabular mt-1" data-testid="dash-earned-so-far">${earnedSoFar.toFixed(2)}</div>
          </div>
          <div>
            <div className="overline gold-text">Remaining balance</div>
            <div className="tabular mt-1 gold-text" data-testid="dash-remaining-balance">${remainingBalance.toFixed(2)}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="overline">Directs</div>
              <div className="tabular mt-1">{user?.direct_referral_count || 0}</div>
            </div>
            <div>
              <div className="overline">Global Club</div>
              <div className="tabular mt-1">{user?.star_club || 0} / 9</div>
            </div>
            <div>
              <div className="overline gold-text">Star Club</div>
              <div className="tabular mt-1 gold-text" data-testid="dash-star-club-level">{sclub?.current_club || 0} / 8</div>
            </div>
          </div>
        </div>
      </div>

      <StarClubPositions />

      <div className="lt-card p-6 flex items-center justify-between flex-wrap gap-4" data-testid="nft-game-card">
        <div className="flex items-center gap-4">
          <Gamepad2 size={28} className="text-[#D4AF37]" />
          <div>
            <div className="overline">NFT Game</div>
            <div className="font-display text-2xl mt-1">Play Card</div>
          </div>
        </div>
        <div className="text-right">
          <div className="overline">Status</div>
          <div
            className={`mt-1 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs uppercase tracking-widest font-medium ${
              nftActive
                ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                : "bg-neutral-500/15 text-neutral-400 border border-neutral-500/30"
            }`}
            data-testid="nft-game-status"
          >
            <span className={`inline-block w-2 h-2 rounded-full ${nftActive ? "bg-emerald-400 animate-pulse" : "bg-neutral-500"}`} />
            {nftActive ? "Active" : "Inactive"}
          </div>
          <div className="text-[10px] text-neutral-500 mt-1 tabular">Principal: ${(Number(playCard?.principal || 0)).toFixed(2)}</div>
        </div>
      </div>
    </div>
  );
}
